/**
 * useUndoRedo.ts - History management hook for automation flow canvas
 *
 * Provides undo/redo functionality using a snapshot-based approach:
 * - Maintains past[], present, and future[] state stacks
 * - Snapshots are taken before meaningful changes
 * - Integrates with auto-save by providing debounce control
 *
 * Related:
 *   - components/AutomationFlowCanvas.tsx - Keyboard shortcuts
 *   - hooks/useAutoSave.ts - Auto-save integration
 */

import { useCallback, useRef, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';

/**
 * Types of actions that can be tracked in history
 */
export type HistoryActionType =
	| 'node_add'
	| 'node_delete'
	| 'node_edit'
	| 'edge_add'
	| 'edge_delete'
	| 'branch_reorder'
	| 'paste'
	| 'batch'
	| 'group_move'
	| 'initial';

/**
 * A snapshot of the canvas state at a point in time
 */
export interface HistorySnapshot {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	timestamp: number;
	actionType: HistoryActionType;
	description: string;
}

/**
 * Internal history state structure
 */
interface HistoryState {
	past: HistorySnapshot[];
	present: HistorySnapshot;
	future: HistorySnapshot[];
}

/**
 * Options for the useUndoRedo hook
 */
export interface UseUndoRedoOptions {
	/** Initial nodes for the canvas */
	initialNodes: Node<BaseNodeData>[];
	/** Initial edges for the canvas */
	initialEdges: Edge[];
	/** Maximum number of history entries to keep (default: 50) */
	maxHistorySize?: number;
}

/**
 * Return type for the useUndoRedo hook
 */
export interface UseUndoRedoReturn {
	/** Current nodes (from present snapshot) */
	nodes: Node<BaseNodeData>[];
	/** Current edges (from present snapshot) */
	edges: Edge[];
	/** Whether undo is available */
	canUndo: boolean;
	/** Whether redo is available */
	canRedo: boolean;
	/** Number of past history entries */
	historySize: number;

	/** Take a snapshot of current state before making changes */
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	/** Undo the last action */
	undo: () => void;
	/** Redo the last undone action */
	redo: () => void;

	/** Update nodes (call after making changes) */
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	/** Update edges (call after making changes) */
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;

	/** Reset history with new initial state */
	resetHistory: (nodes: Node<BaseNodeData>[], edges: Edge[]) => void;

	/** Whether an undo/redo operation just occurred (for auto-save debouncing) */
	isUndoRedoOperation: boolean;
	/** Clear the undo/redo operation flag */
	clearUndoRedoFlag: () => void;
}

const DEFAULT_MAX_HISTORY_SIZE = 50;

/**
 * Deep clone nodes and edges for immutable snapshots
 */
function cloneState(nodes: Node<BaseNodeData>[], edges: Edge[]): { nodes: Node<BaseNodeData>[]; edges: Edge[] } {
	return {
		nodes: nodes.map(node => ({
			...node,
			position: { ...node.position },
			data: structuredClone(node.data),
		})),
		edges: edges.map(edge => ({
			...edge,
			data: edge.data ? structuredClone(edge.data) : undefined,
		})),
	};
}

/**
 * Create an initial snapshot from nodes and edges
 */
function createInitialSnapshot(nodes: Node<BaseNodeData>[], edges: Edge[]): HistorySnapshot {
	const cloned = cloneState(nodes, edges);
	return {
		nodes: cloned.nodes,
		edges: cloned.edges,
		timestamp: Date.now(),
		actionType: 'initial',
		description: 'Initial state',
	};
}

/**
 * Hook for managing undo/redo history in the automation flow canvas.
 *
 * @example
 * ```typescript
 * const {
 *   nodes,
 *   edges,
 *   canUndo,
 *   canRedo,
 *   takeSnapshot,
 *   undo,
 *   redo,
 *   setNodes,
 *   setEdges,
 * } = useUndoRedo({ initialNodes, initialEdges });
 *
 * // Before deleting a node:
 * takeSnapshot('node_delete', 'Delete email step');
 * setNodes(nodes.filter(n => n.id !== nodeId));
 *
 * // In keyboard handler:
 * if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
 *   if (e.shiftKey) redo();
 *   else undo();
 * }
 * ```
 */
export function useUndoRedo({
	initialNodes,
	initialEdges,
	maxHistorySize = DEFAULT_MAX_HISTORY_SIZE,
}: UseUndoRedoOptions): UseUndoRedoReturn {
	// History state with past, present, future
	const [history, setHistory] = useState<HistoryState>(() => ({
		past: [],
		present: createInitialSnapshot(initialNodes, initialEdges),
		future: [],
	}));

	// Current working state (may differ from present snapshot until next snapshot)
	const [currentNodes, setCurrentNodes] = useState<Node<BaseNodeData>[]>(initialNodes);
	const [currentEdges, setCurrentEdges] = useState<Edge[]>(initialEdges);

	// Flag for auto-save integration
	const [isUndoRedoOperation, setIsUndoRedoOperation] = useState(false);

	// Ref for accessing current state in callbacks without stale closures
	const currentStateRef = useRef({ nodes: currentNodes, edges: currentEdges });
	currentStateRef.current = { nodes: currentNodes, edges: currentEdges };

	/**
	 * Take a snapshot of the current state before making changes.
	 * This captures the "before" state so undo can restore it.
	 */
	const takeSnapshot = useCallback(
		(actionType: HistoryActionType, description: string) => {
			const { nodes, edges } = currentStateRef.current;
			const cloned = cloneState(nodes, edges);

			const snapshot: HistorySnapshot = {
				nodes: cloned.nodes,
				edges: cloned.edges,
				timestamp: Date.now(),
				actionType,
				description,
			};

			setHistory(prev => {
				const newHistory: HistoryState = {
					// Keep past entries up to max size, add the current snapshot (before the change)
					past: [...prev.past.slice(-(maxHistorySize - 1)), snapshot],
					present: snapshot,
					// Clear future on new action (can't redo after new changes)
					future: [],
				};
				return newHistory;
			});
		},
		[maxHistorySize]
	);

	/**
	 * Undo the last action by restoring the previous snapshot.
	 */
	const undo = useCallback(() => {
		setHistory(prev => {
			if (prev.past.length === 0) {
				return prev;
			}

			// Get the last past snapshot
			const previousSnapshot = prev.past[prev.past.length - 1];
			const newPast = prev.past.slice(0, -1);

			// Create snapshot of current state for future (redo)
			const { nodes, edges } = currentStateRef.current;
			const currentSnapshot: HistorySnapshot = {
				...cloneState(nodes, edges),
				timestamp: Date.now(),
				actionType: prev.present.actionType,
				description: prev.present.description,
			};

			// Update current nodes/edges synchronously within the same state update
			// to avoid race conditions with separate setState calls
			setCurrentNodes(previousSnapshot.nodes);
			setCurrentEdges(previousSnapshot.edges);

			return {
				past: newPast,
				present: previousSnapshot,
				future: [currentSnapshot, ...prev.future],
			};
		});

		setIsUndoRedoOperation(true);
	}, []);

	/**
	 * Redo the last undone action.
	 */
	const redo = useCallback(() => {
		setHistory(prev => {
			if (prev.future.length === 0) {
				return prev;
			}

			// Get the first future snapshot
			const nextSnapshot = prev.future[0];
			const newFuture = prev.future.slice(1);

			// Update current nodes/edges synchronously within the same state update
			// to avoid race conditions with separate setState calls
			setCurrentNodes(nextSnapshot.nodes);
			setCurrentEdges(nextSnapshot.edges);

			// Current present becomes part of past
			return {
				past: [...prev.past, prev.present],
				present: nextSnapshot,
				future: newFuture,
			};
		});

		setIsUndoRedoOperation(true);
	}, []);

	/**
	 * Reset history with new initial state (e.g., when loading a different template)
	 */
	const resetHistory = useCallback((nodes: Node<BaseNodeData>[], edges: Edge[]) => {
		const initialSnapshot = createInitialSnapshot(nodes, edges);
		setHistory({
			past: [],
			present: initialSnapshot,
			future: [],
		});
		setCurrentNodes(nodes);
		setCurrentEdges(edges);
	}, []);

	/**
	 * Clear the undo/redo operation flag (call after auto-save debounce)
	 */
	const clearUndoRedoFlag = useCallback(() => {
		setIsUndoRedoOperation(false);
	}, []);

	return {
		nodes: currentNodes,
		edges: currentEdges,
		canUndo: history.past.length > 0,
		canRedo: history.future.length > 0,
		historySize: history.past.length,
		takeSnapshot,
		undo,
		redo,
		setNodes: setCurrentNodes,
		setEdges: setCurrentEdges,
		resetHistory,
		isUndoRedoOperation,
		clearUndoRedoFlag,
	};
}
